import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import CustomModal from '../ui/modal/modal.component';
import { useDemo } from '../../api/demo';
import { ERROR_SNACKBAR } from '../../consts';

interface ClearDemoModalProps {
  opened: boolean;
  setOpened: (value: boolean) => void;
}

/**
 * Confirms clearing the demo: everything the workspace holds goes, including
 * what was added while trying it, and only users, permission groups, settings
 * and integrations stay (app.services.demo.clear).
 */
export const ClearDemoModal = ({ opened, setOpened }: ClearDemoModalProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { clear, loading } = useDemo();

  const handleClear = async () => {
    const { success, errorData } = await clear();
    if (!success) {
      enqueueSnackbar(
        errorData?.message || t('Could not clear the demo data. Please try again.'),
        ERROR_SNACKBAR
      );
      return;
    }
    // A full reload, so no page keeps showing records that are gone.
    window.location.assign('/home');
  };

  return (
    <CustomModal
      opened={opened}
      setOpened={(value) => !loading && setOpened(value)}
      title={t('Clear demo data')}
      width="min(520px, 92vw)"
      minWidth="min(520px, 92vw)"
      minHeight="auto"
      content={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2">
            {t(
              'Everything in the workspace will be deleted: clients, equipment, contracts, settlements, invoices, service jobs and parts - including anything you added while trying the demo. This cannot be undone.'
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              'Users and permission groups stay, and so do the workspace settings and integrations. The settings the demo changed go back to how they were before it.'
            )}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
            <Button onClick={() => setOpened(false)} disabled={loading}>
              {t('Cancel')}
            </Button>
            <Button variant="contained" color="error" onClick={handleClear} loading={loading}>
              {t('Clear demo data')}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};
